import React, { useEffect, FC, useRef } from 'react';
import { SearchInputSmall } from '../common/SearchInputSmall';
import { DateRangePicker, SelectPicker } from 'rsuite';
import { isAfter, endOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { timeZonesList } from '../profileSettings/ProfileSettingsPreferences';
import { getFormattedDate, makeSearchParams, parseSearchParams } from '../../utils/utilities';
import { useDisputesContext } from '../../store/disputes/disputes-context';
import { useDebounce } from 'use-debounce';
import { FilterComponent } from '../FilterComponent';
import clsx from 'clsx';
import { ButtonComponent } from '../ButtonComponent';
import { processedSelector } from '../../store/disputes/disputes-variables';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';

export interface IDisputeQueuesFilters {
    partnerId?: string;
    timezone?: string;
    from?: Date | string;
    to?: Date | string;
    isFinished?: string;
    query?: string;
    pageSize?: string;
    pageNumber?: string;
}

interface Props {
    openAddQueueModal?: () => void,
    partnerId: string,
    setPartnerId: (partnerId: string) => void,
    timezone: string,
    setTimezone: (timezone: string) => void,
    from: Date | string,
    setFrom: (from: Date | string) => void,
    to: Date | string,
    setTo: (to: Date | string) => void,
    isFinished: string,
    setIsFinished: (isFinished: string) => void,
    query: string,
    setQuery: (query: string) => void,
}

export const DisputeQueuesFilterSection: FC<Props> = (props) => {
    const {
        openAddQueueModal, partnerId, setPartnerId, timezone, setTimezone, from,
        setFrom, to, setTo, isFinished, setIsFinished, query, setQuery,
    } = props;
    const { t } = useTranslation();
    const isMounted = useRef(false);
    const { pathname, search } = useLocation();
    const navigate = useNavigate();


    const { merchantState: { whitelabelPartnersListSelector }} = useMerchants();

    const filtersFromUrl: IDisputeQueuesFilters = parseSearchParams(search) || {};


    const { disputesContextState } = useDisputesContext();
    const { isDisputeQueuesLoading } = disputesContextState;

    const [value] = useDebounce(query.trim(), 1000);

    useEffect(() => {
        if (!isMounted.current) return;

        const params = { ...filtersFromUrl };

        params.query = query;
        params.from = from;
        params.to = to;
        params.partnerId = partnerId;
        params.isFinished = isFinished;
        params.timezone = timezone;
        params.pageNumber = '1';

        if (!params?.isFinished) {
            params.isFinished = 'null';
        }

        navigate(`${pathname}?${makeSearchParams(params)}`);

    }, [partnerId, from, isFinished, timezone, value]);

    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
        }
    }, []);

    const isSelectedFilters = from || query || partnerId || isFinished || (timezone && timezone !== 'UTC');

    const handleCleanFilter = (): void => {

        setQuery('');
        setFrom(null);
        setTo(null);
        setIsFinished('');
        setPartnerId('');
        setTimezone('UTC');

        const params: IDisputeQueuesFilters = {
            isFinished: 'null',
            timezone: 'UTC',
            pageNumber: '1',
        };


        navigate(`${pathname}?${makeSearchParams(params)}`);
    };

    const setDateFilter = (value: Date[]): void => {
        const dateFrom = value?.[0] ? getFormattedDate(value[0], 'yyyy-MM-dd HH:mm:ss') : null;
        const dateTo = value?.[1] ? getFormattedDate(value[1], 'yyyy-MM-dd HH:mm:ss') : null;

        setFrom(dateFrom);
        setTo(dateTo);
    };

    return (
        <div className="card bg-transparent fc-disputes-filter">
            <div className="card-body py-0 px-0">
                <div className="d-flex align-items-center justify-content-between block-width application-selector mb-5 w-100">
                    <SearchInputSmall
                        placeholder={t('DISPUTES.SEARCH_PLACEHOLDER')}
                        additionalClass=""
                        searchValue={query}
                        setSearchValue={setQuery}
                    />

                    <ButtonComponent
                        buttonName="Add Queue"
                        onButtonClick={openAddQueueModal}
                        additionalClass="btn-sm"
                    />
                </div>

                <div className="d-flex align-items-center  fc-filter-line">

                    <FilterComponent value={partnerId} label={t('MENU.PARTNERS')} labelPlacement="integrated">
                        <SelectPicker
                          data={whitelabelPartnersListSelector}
                          appearance="subtle"
                          placement="bottomStart"
                          placeholder={t('MENU.PARTNERS')}
                          onSelect={(pid): void => setPartnerId(pid)}
                          className={clsx('fc-selector timezone-selector', partnerId ? 'fc-selector-cleanable' : 'fc-added-icon')}
                          onClean={(): void => setPartnerId('')}
                          value={partnerId}
                          disabled={isDisputeQueuesLoading}
                        />
                    </FilterComponent>

                    <FilterComponent value={from} label="Created" labelPlacement="integrated">
                        <div className={clsx('fc-datepicker fc-range-datepicker', from && 'fc-added-icon')}>
                            <DateRangePicker
                                placeholder="Created"
                                placement="auto"
                                defaultCalendarValue={[
                                    new Date(getFormattedDate(new Date().setHours(0, 0, 0), 'yyyy-MM-dd HH:mm:ss')),
                                    new Date(getFormattedDate(new Date().setHours(23, 59, 59), 'yyyy-MM-dd HH:mm:ss'))
                                ]}
                                onChange={(value): void => setDateFilter(value)}
                                onClean={(): void => setDateFilter([null, null])}
                                shouldDisableDate={(date): boolean => isAfter(date, endOfDay(new Date()))}
                                value={!from || !to ? null : [new Date(from), new Date(to)]}
                                format="yyyy-MM-dd HH:mm:ss"
                                disabled={isDisputeQueuesLoading}
                            />
                        </div>
                    </FilterComponent>

                    <FilterComponent value={timezone} label="Time Zone" labelPlacement="integrated">
                        <SelectPicker
                            data={timeZonesList}
                            appearance="subtle"
                            placement="bottomEnd"
                            placeholder='Time Zone'
                            onSelect={(timezone): void => setTimezone(timezone)}
                            className="fc-selector timezone-selector"
                            onClean={(): void => setTimezone('UTC')}
                            cleanable={false}
                            // defaultValue={disputeQueueTimezone}
                            value={timezone}
                            disabled={isDisputeQueuesLoading}
                        />
                    </FilterComponent>

                    <FilterComponent value={isFinished} label="Processed" labelPlacement="integrated">
                        <SelectPicker
                            data={processedSelector}
                            appearance="subtle"
                            placement="bottomEnd"
                            placeholder='Processed'
                            onSelect={(value): void => setIsFinished(value)}
                            className={clsx('fc-selector timezone-selector', isFinished ? 'fc-selector-cleanable' : 'fc-added-icon')}
                            onClean={(): void => setIsFinished('')}
                            searchable={false}
                            // defaultValue={isProcessedFilter}
                            value={isFinished}
                            disabled={isDisputeQueuesLoading}
                        />
                    </FilterComponent>

                    {
                        isSelectedFilters && (
                            <div
                              className="text-danger text-decoration-underline cursor-pointer"
                              onClick={handleCleanFilter}>
                                Clear filters
                            </div>
                      )
                    }
                </div>
            </div>
        </div>
    );
};
